/**
 * Utility functions for habit tracking across journal entries
 */

// Map priority number to category key
export const getPriorityCategory = (transformationData) => {
    if (!transformationData) return null;

    const priorities = [
        { priority: transformationData.nutritionPriority, category: 'nutrition', key: 'nutritionHabits' },
        { priority: transformationData.sleepPriority, category: 'sleep', key: 'sleepHabits' },
        { priority: transformationData.exercisePriority, category: 'exercise', key: 'exerciseHabits' },
        { priority: transformationData.stressPriority, category: 'stress', key: 'stressHabits' }
    ].filter(p => p.priority); // Filter out empty priorities

    // Find the category with priority 1
    const topPriority = priorities.find(p => parseInt(p.priority) === 1);
    return topPriority || null;
};

// Get ALL categories with priority 1
export const getAllPriority1Categories = (transformationData) => {
    if (!transformationData) return [];

    const priorities = [
        { priority: transformationData.nutritionPriority, category: 'nutrition', key: 'nutritionHabits', name: 'Nutrition' },
        { priority: transformationData.sleepPriority, category: 'sleep', key: 'sleepHabits', name: 'Sleep' },
        { priority: transformationData.exercisePriority, category: 'exercise', key: 'exerciseHabits', name: 'Exercise' },
        { priority: transformationData.stressPriority, category: 'stress', key: 'stressHabits', name: 'Stress Management' }
    ].filter(p => p.priority && parseInt(p.priority) === 1);

    return priorities;
};

// Get habit labels by category
export const getHabitLabels = () => ({
    nutrition: {
        meals: 'Eat (two / three) meals a day',
        fasting: 'Maintain a (12 / 13 / 14 / 15 / 16)-hour fast. These are the hours after dinner where I will not eat or drink until breakfast. This includes all foods and drinks except water',
        noSnacking: 'Not snack between meals. Any snacks will be eaten directly after meals',
        avoidUnhealthy: 'Avoid unhealthy (snacks / drinks / snacks and drinks)',
        avoidAlcohol: 'Avoid alcohol',
        fibreVeg: 'Eat at least (one / two / three) sources of (fibre / vegetable) in each meal',
        protein: 'Eat at least one source of protein in each meal',
        proteinGrams: 'Eat a minimum of _____ grams of protein per day',
        healthyFat: 'Eat at least one source of healthy fat in each meal',
        equalCarbs: 'Have (equal / less) carbs on my plate compared to protein',
        avoidProcessed: 'Avoid ultra-processed foods',
        avoidIntolerances: 'Avoid my food intolerances',
        water: 'Drink _____ litres of water per day'
    },
    sleep: {
        sunlight: 'Get direct sunlight in the morning (hrs / afternoon)',
        movement: 'Practice my favourite form of movement during the day',
        noCaffeine: 'Have no caffeine within 10 hours before bed',
        consistent: 'Consistently go to sleep at _____ and wake up at _____',
        windDown: 'Wind down and prepare for bed _____ hours before sleeping',
        homeDim: 'Keep my home as dim as possible in the evening and as dark as possible while sleeping',
        avoidScreens: 'Avoid screens _____ hours before sleeping',
        avoidStimulating: 'Avoid anything too stimulating or anxiety-inducing _____ hours before bed (news / info / exercise / scary or thought-provoking media)',
        lightDinner: 'Have an earlier and/or lighter dinner',
        avoidAlcoholBeforeBed: 'Avoid alcohol _____ hours before bed',
        eveningHabits: 'Have (one / two / three) evening relaxation habits like (journaling / reading / meditating / praying / stretching / breathing)'
    },
    exercise: {
        stretch: 'Stretch for _____ minutes _____ times per week',
        physical: 'Do any form of physical activity for _____ minutes _____ times a week',
        steps: 'Reach _____ steps per day',
        walk: 'Walk for _____ (minutes / km / miles) _____ times a week',
        run: 'Run for _____ (minutes / km / miles) _____ times a week',
        hiit: 'Do a (15 / 20 / 30)-minute HIIT work-out _____ times a week',
        sport: 'Participate in my sport of choice which is _____ I will do this _____ times a week',
        buildMuscles: 'Do an exercise that builds muscles _____ times a week',
        mobility: 'Do some mobility exercises _____ times per week',
        exerciseRestDay: 'Have at least one day of rest and recovery'
    },
    stress: {
        relax: 'Take at least one hour per day to relax',
        restDay: 'Have a full day of rest each week',
        morningTime: 'Spend _____ minutes with myself each morning',
        schedule: 'Organise my schedule in advance - e.g (daily / weekly) to-do list',
        breathingRoom: 'Leave some breathing room in my schedule and avoid overcommitting myself',
        boundaries: 'Enforce boundaries to protect my mental and emotional health',
        avoidMedia: 'Avoid media that is increasing my stress load',
        nature: 'Spend time in nature _____ times a week',
        express: 'Express my thoughts and feelings to (a trusted friend / health professional / journal / recorder / self) _____ times a week',
        breathwork: 'Do breathwork _____ times (a day / week)',
        grateful: 'List 10 things that I am grateful for daily',
        hobbies: 'Engage in my hobbies _____ times a week',
        loveLanguage: 'Give myself a treat according to my love language _____ times a week'
    }
});

// Get selected habits from a category
export const getSelectedHabits = (data, categoryKey) => {
    if (!data || !data[categoryKey]) return [];

    const habits = data[categoryKey];
    const labels = getHabitLabels();

    // Determine which category based on key
    let category = '';
    if (categoryKey === 'nutritionHabits') category = 'nutrition';
    else if (categoryKey === 'sleepHabits') category = 'sleep';
    else if (categoryKey === 'exerciseHabits') category = 'exercise';
    else if (categoryKey === 'stressHabits') category = 'stress';

    if (!category) return [];

    return habits.map(habitKey => ({
        key: habitKey,
        label: labels[category][habitKey] || habitKey
    }));
};

// Get all selected habits from all categories in a CheckIn
export const getAllSelectedHabits = (checkinData) => {
    if (!checkinData) return [];

    const allHabits = [];
    const categories = [
        { key: 'nutritionHabits', name: 'Nutrition' },
        { key: 'sleepHabits', name: 'Sleep' },
        { key: 'exerciseHabits', name: 'Exercise' },
        { key: 'stressHabits', name: 'Stress Management' }
    ];

    categories.forEach(cat => {
        const habits = getSelectedHabits(checkinData, cat.key);
        habits.forEach(habit => {
            allHabits.push({
                ...habit,
                category: cat.name
            });
        });
    });

    return allHabits;
};

// Format habits for dropdown display
export const formatHabitsForDropdown = (habits) => {
    return habits.map(h => ({
        value: h.key,
        label: h.label //h.category ? `[${h.category}] ${h.label}` : h.label
    }));
};

// Get all habits from Transformation (all categories with selected habits)
export const getAllTransformationHabits = (transformationData) => {
    if (!transformationData) return [];

    const allHabits = [];
    const categories = [
        { key: 'nutritionHabits', name: 'Nutrition' },
        { key: 'sleepHabits', name: 'Sleep' },
        { key: 'exerciseHabits', name: 'Exercise' },
        { key: 'stressHabits', name: 'Stress Management' }
    ];

    categories.forEach(cat => {
        if (transformationData[cat.key] && Array.isArray(transformationData[cat.key])) {
            const habits = getSelectedHabits(transformationData, cat.key);
            habits.forEach(habit => {
                allHabits.push({
                    ...habit,
                    category: cat.name
                });
            });
        }
    });

    return allHabits;
};

// Get the full habits list for a category in order (matches Transformation.js order)
export const getCategoryHabitsInOrder = (category) => {
    const habitLists = {
        nutrition: [
            'Eat (two / three) meals a day',
            'Maintain a (12 / 13 / 14 / 15 / 16)-hour fast. These are the hours after dinner where I will not eat or drink until breakfast. This includes all foods and drinks except water',
            'Not snack between meals. Any snacks will be eaten directly after meals',
            'Avoid unhealthy (snacks / drinks / snacks and drinks)',
            'Avoid alcohol',
            'Eat at least (one / two / three) sources of (fibre / vegetable) in each meal',
            'Eat at least one source of protein in each meal',
            'Eat a minimum of _____ grams of protein per day',
            'Eat at least one source of healthy fat in each meal',
            'Have (equal / less) carbs on my plate compared to protein',
            'Avoid ultra-processed foods',
            'Avoid my food intolerances',
            'Drink _____ litres of water per day'
        ],
        sleep: [
            'Get direct sunlight in the morning (hrs / afternoon)',
            'Practice my favourite form of movement during the day',
            'Have no caffeine within 10 hours before bed',
            'Consistently go to sleep at _____ and wake up at _____',
            'Wind down and prepare for bed _____ hours before sleeping',
            'Keep my home as dim as possible in the evening and as dark as possible while sleeping',
            'Avoid screens _____ hours before sleeping',
            'Avoid anything too stimulating or anxiety-inducing _____ hours before bed (news / info / exercise / scary or thought-provoking media)',
            'Have an earlier and/or lighter dinner',
            'Avoid alcohol _____ hours before bed',
            'Have (one / two / three) evening relaxation habits like (journaling / reading / meditating / praying / stretching / breathing)'
        ],
        exercise: [
            'Stretch for _____ minutes _____ times per week',
            'Do any form of physical activity for _____ minutes _____ times a week',
            'Reach _____ steps per day',
            'Walk for _____ (minutes / km / miles) _____ times a week',
            'Run for _____ (minutes / km / miles) _____ times a week',
            'Do a (15 / 20 / 30)-minute HIIT work-out _____ times a week',
            'Participate in my sport of choice which is _____ I will do this _____ times a week',
            'Do an exercise that builds muscles _____ times a week',
            'Do some mobility exercises _____ times per week',
            'Have at least one day of rest and recovery'
        ],
        stress: [
            'Take at least one hour per day to relax',
            'Have a full day of rest each week',
            'Spend _____ minutes with myself each morning',
            'Organise my schedule in advance - e.g (daily / weekly) to-do list',
            'Leave some breathing room in my schedule and avoid overcommitting myself',
            'Enforce boundaries to protect my mental and emotional health',
            'Avoid media that is increasing my stress load',
            'Spend time in nature _____ times a week',
            'Express my thoughts and feelings to (a trusted friend / health professional / journal / recorder / self) _____ times a week',
            'Do breathwork _____ times (a day / week)',
            'List 10 things that I am grateful for daily',
            'Engage in my hobbies _____ times a week',
            'Give myself a treat according to my love language _____ times a week'
        ]
    };

    return habitLists[category] || [];
};

// Get filled habit text from Transformation data using habit text and category
export const getFilledHabitText = (habitText, category, transformationData) => {
    if (!transformationData) return habitText;

    // Find the index of this habit in the full habits list
    const habitsList = getCategoryHabitsInOrder(category);
    let habitIndex = -1;

    // Match habit by comparing the start of the text
    for (let i = 0; i < habitsList.length; i++) {
        if (habitText.startsWith(habitsList[i])) {
            habitIndex = i;
            break;
        }
        // Special case: "Consistently go to sleep" has text variations
        if (habitsList[i].includes('Consistently go to sleep') && habitText.includes('Consistently go to sleep')) {
            habitIndex = i;
            break;
        }
    }

    if (habitIndex === -1) {
        console.warn('Habit not found in category list:', habitText, 'Category:', category);
        return habitText;
    }

    // Build the key prefix for accessing transformation data
    const keyPrefix = `${category}_habit_${habitIndex}`;

    // Helper function to safely get value
    const getValue = (field) => transformationData[`${keyPrefix}_${field}`];

    // === NUTRITION HABITS ===

    // "Eat (two / three) meals a day"
    if (habitText.includes('Eat (two / three) meals a day') || habitText.includes('Eat (two/three) meals a day')) {
        const meals = getValue('meals');
        if (meals) {
            return habitText.replace(/\(two\s*\/\s*three\)/i, meals);
        }
    }

    // "Maintain a (12 / 13 / 14 / 15 / 16)-hour fast"
    if (habitText.includes('hour fast')) {
        const hours = getValue('hours');
        if (hours) {
            return habitText.replace(/\(12\s*\/\s*13\s*\/\s*14\s*\/\s*15\s*\/\s*16\)/i, hours);
        }
    }

    // "Avoid unhealthy (snacks / drinks / snacks and drinks)"
    if (habitText.includes('Avoid unhealthy')) {
        const avoid = getValue('avoid');
        if (avoid) {
            return habitText.replace(/\(snacks\s*\/\s*drinks\s*\/\s*snacks and drinks\)/i, avoid);
        }
    }

    // "Eat at least (one / two / three) sources of (fibre / vegetable)"
    if (habitText.includes('sources of')) {
        const quantity = getValue('quantity');
        if (quantity) {
            return habitText.replace(/\(one\s*\/\s*two\s*\/\s*three\)/i, quantity);
        }
    }

    // "Eat a minimum of _____ grams of protein per day"
    if (habitText.includes('grams of protein')) {
        const protein = getValue('protein');
        if (protein) {
            return habitText.replace('_____', protein);
        }
    }

    // "Have (equal / less) carbs on my plate"
    if (habitText.includes('carbs on my plate')) {
        const carbs = getValue('carbs');
        if (carbs) {
            return habitText.replace(/\(equal\s*\/\s*less\)/i, carbs);
        }
    }

    // "Drink _____ litres of water per day"
    if (habitText.includes('litres of water')) {
        const water = getValue('water');
        if (water) {
            return habitText.replace('_____', water);
        }
    }

    // === SLEEP HABITS ===

    // "Get direct sunlight in the morning" - no user input, return as is
    if (habitText.includes('Get direct sunlight in the morning')) {
        return habitText;
    }

    // "Consistently go to sleep at _____ and wake up at _____"
    if (habitText.includes('go to sleep')) {
        const bedtime = getValue('bedtime');
        const waketime = getValue('waketime');

        if (bedtime || waketime) {
            // Handle different text patterns
            if (habitText.match(/go to sleep at _____ and wake up at _____/)) {
                return habitText.replace(
                    /go to sleep at _____ and wake up at _____/,
                    `go to sleep at ${bedtime || '_____'} and wake up at ${waketime || '_____'}`
                );
            }
            if (habitText.match(/go to sleep _____ at and wake up at _____/)) {
                return habitText.replace(
                    /go to sleep _____ at and wake up at _____/,
                    `go to sleep ${bedtime || '_____'} at and wake up at ${waketime || '_____'}`
                );
            }
        }
    }

    // "Wind down and prepare for bed _____ hours before sleeping"
    if (habitText.includes('Wind down and prepare for bed')) {
        const winddown = getValue('winddown');
        if (winddown) {
            return habitText.replace('_____', winddown);
        }
    }

    // "Avoid screens _____ hours before sleeping"
    if (habitText.includes('Avoid screens') && habitText.includes('before sleeping')) {
        const screens = getValue('screens');
        if (screens) {
            return habitText.replace('_____', screens);
        }
    }

    // "Avoid anything too stimulating _____ hours before bed"
    if (habitText.includes('too stimulating')) {
        const stimulating = getValue('stimulating');
        if (stimulating) {
            return habitText.replace('_____', stimulating);
        }
    }

    // "Avoid alcohol _____ hours before bed"
    if (habitText.includes('Avoid alcohol') && habitText.includes('before bed')) {
        const alcohol = getValue('alcohol');
        if (alcohol) {
            return habitText.replace('_____', alcohol);
        }
    }

    // "Have (one / two / three) evening relaxation habits"
    if (habitText.includes('evening relaxation habits')) {
        const count = getValue('count');
        const activities = getValue('activities');

        if (activities && Array.isArray(activities) && activities.length > 0) {
            const activityList = activities.join(', ');
            const displayCount = count || (activities.length === 1 ? 'one' : activities.length === 2 ? 'two' : 'three');
            return `Have ${displayCount} evening relaxation habits like ${activityList}`;
        }
        if (count) {
            return habitText.replace(/\(one\s*\/\s*two\s*\/\s*three\)/i, count);
        }
    }

    // === EXERCISE HABITS ===

    // "Stretch for _____ minutes _____ times per week"
    if (habitText.includes('Stretch for') && habitText.includes('times per week')) {
        const minutes = getValue('minutes');
        const times = getValue('times');
        if (minutes || times) {
            return `Stretch for ${minutes || '_____'} minutes ${times || '_____'} times per week`;
        }
    }

    // "Do any form of physical activity for _____ minutes _____ times a week"
    if (habitText.includes('physical activity')) {
        const minutes = getValue('minutes');
        const times = getValue('times');
        if (minutes || times) {
            return `Do any form of physical activity for ${minutes || '_____'} minutes ${times || '_____'} times a week`;
        }
    }

    // "Reach _____ steps per day"
    if (habitText.includes('steps per day')) {
        const steps = getValue('steps');
        if (steps) {
            return habitText.replace('_____', steps);
        }
    }

    // "Walk for _____ (minutes / km / miles) _____ times a week"
    if (habitText.includes('Walk for')) {
        const amount = getValue('amount');
        const unit = getValue('unit');
        const times = getValue('times');
        if (amount || unit || times) {
            return `Walk for ${amount || '_____'} ${unit || '(minutes / km / miles)'} ${times || '_____'} times a week`;
        }
    }

    // "Run for _____ (minutes / km / miles) _____ times a week"
    if (habitText.includes('Run for')) {
        const amount = getValue('amount');
        const unit = getValue('unit');
        const times = getValue('times');
        if (amount || unit || times) {
            return `Run for ${amount || '_____'} ${unit || '(minutes / km / miles)'} ${times || '_____'} times a week`;
        }
    }

    // "Do a (15 / 20 / 30)-minute HIIT work-out"
    if (habitText.includes('HIIT work-out')) {
        const duration = getValue('duration');
        const times = getValue('times');
        if (duration || times) {
            return `Do a ${duration || '(15 / 20 / 30)'}-minute HIIT work-out ${times || '_____'} times a week`;
        }
    }

    // "Participate in my sport of choice which is _____ I will do this _____ times a week"
    if (habitText.includes('sport of choice')) {
        const sport = getValue('sport');
        const times = getValue('times');
        if (sport || times) {
            return `Participate in my sport of choice which is ${sport || '_____'} I will do this ${times || '_____'} times a week`;
        }
    }

    // "Do an exercise that builds muscles _____ times a week"
    if (habitText.includes('builds muscles')) {
        const times = getValue('times');
        if (times) {
            return habitText.replace('_____', times);
        }
    }

    // "Do some mobility exercises _____ times per week"
    if (habitText.includes('mobility exercises')) {
        const times = getValue('times');
        if (times) {
            return habitText.replace('_____', times);
        }
    }

    // === STRESS MANAGEMENT HABITS ===

    // "Spend _____ minutes with myself each morning"
    if (habitText.includes('minutes with myself each morning')) {
        const minutes = getValue('minutes');
        if (minutes) {
            return habitText.replace('_____', minutes);
        }
    }

    // "Organise my schedule in advance - e.g (daily / weekly) to-do list"
    if (habitText.includes('Organise my schedule')) {
        const type = getValue('type');
        if (type) {
            return `Organise my schedule in advance - e.g ${type} to-do list`;
        }
    }

    // "Spend time in nature _____ times a week"
    if (habitText.includes('time in nature')) {
        const times = getValue('times');
        if (times) {
            return habitText.replace('_____', times);
        }
    }

    // "Express my thoughts and feelings to (...) _____ times a week"
    if (habitText.includes('Express my thoughts and feelings')) {
        const method = getValue('method');
        const times = getValue('times');
        if (method || times) {
            return `Express my thoughts and feelings to ${method || '(a trusted friend / health professional / journal / recorder / self)'} ${times || '_____'} times a week`;
        }
    }

    // "Do breathwork _____ times (a day / week)"
    if (habitText.includes('Do breathwork')) {
        const times = getValue('times');
        const frequency = getValue('frequency');
        if (times || frequency) {
            return `Do breathwork ${times || '_____'} times ${frequency || '(a day / week)'}`;
        }
    }

    // "Engage in my hobbies _____ times a week"
    if (habitText.includes('Engage in my hobbies')) {
        const times = getValue('times');
        if (times) {
            return habitText.replace('_____', times);
        }
    }

    // "Give myself a treat according to my love language _____ times a week"
    if (habitText.includes('myself a treat')) {

        const times = getValue('times');
        console.log('times', times);
        if (times) {
            return habitText.replace('_____', times);
        }
    }

    // If no replacements were made, return original text
    return habitText;
};



